import { useRef, useState, useEffect } from 'react'
import {
  Download,
  Printer,
  X,
  FileText,
  Check,
  Edit2,
  Eye,
  RotateCcw,
  Plus,
  Trash2,
} from 'lucide-react'
import html2pdf from 'html2pdf.js'
import { toast } from 'react-hot-toast'
import type { BookingRecord } from '../../../types'
import { formatBookingDate, formatBookingTime24 } from '../../../utils/format'

const formatInvoiceEuro = (value: number) =>
  `€${Number(value || 0).toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

export type VatRate = 10 | 20

export interface InvoiceItem {
  id: string
  description: string
  subtitle?: string
  taxNote?: string
  qty?: number | null
  unitPrice: number
  isTax?: boolean
}

export const isTaxItem = (item: InvoiceItem): boolean => {
  if (item.isTax === true) return true
  if (item.id === 'vat' || item.id === 'tax') return true
  const desc = (item.description || '').toLowerCase()
  return desc.includes('tax') || desc.includes('vat')
}

export const getItemAmount = (item: InvoiceItem): number => {
  if (isTaxItem(item)) {
    return Number(item.unitPrice) || 0
  }
  return (Number(item.qty) || 0) * (Number(item.unitPrice) || 0)
}

/**
 * Calculates Net amount and VAT amount from a Gross total that includes statutory VAT.
 * Net = Gross / (1 + rate / 100)
 * VAT = Gross - Net
 */
export const calculateVatBreakdown = (
  grossAmount: number,
  vatRate: VatRate = 10,
): { tripNetPrice: number; vatAmount: number } => {
  const safeGross = Number(grossAmount) || 0
  const tripNetPrice = Math.round((safeGross / (1 + vatRate / 100)) * 100) / 100
  const vatAmount = Math.round((safeGross - tripNetPrice) * 100) / 100
  return { tripNetPrice, vatAmount }
}

export interface EditableInvoiceData {
  companyName: string
  companyAddress: string
  companyPhones: string
  companyEmails: string

  invoiceTitle: string
  invoiceNumber: string
  invoiceDate: string
  bookingReference: string
  paymentStatus: 'paid' | 'unpaid' | 'pending'

  customerName: string
  customerEmail: string
  customerPhone?: string
  customerAddress?: string

  serviceTitle: string
  dateTimeText: string
  vehicleText: string

  pickupLocation: string
  dropoffLocation: string

  items: InvoiceItem[]

  vatRate: VatRate
  manualAmountPaid?: number | null

  notes?: string
  footerText: string
}

const createInitialInvoiceData = (
  booking: BookingRecord,
  vatRate: VatRate = 10,
): EditableInvoiceData => {
  const dateStr = formatBookingDate(booking.scheduledAt)
  const timeStr = formatBookingTime24(booking.scheduledAt)
  const totalAmount = Number(booking.amountEur) || 0
  const { tripNetPrice, vatAmount } = calculateVatBreakdown(totalAmount, vatRate)

  const routeSubtitle =
    booking.route?.from && booking.route?.to
      ? `${booking.route.from} → ${booking.route.to}`
      : booking.route?.from || booking.route?.to || ''

  return {
    companyName: 'Salzburg Cab',
    companyAddress: 'Rudolf Spängler Straße 10, 5020 Salzburg, Austria',
    companyPhones: '+43 720 7750 53 • +43 676 334 7141',
    companyEmails: 'office@salzburgcab.com • info@salzburgcab.com',

    invoiceTitle: 'INVOICE',
    invoiceNumber: `INV-${booking.reference}`,
    invoiceDate: dateStr,
    bookingReference: `#${booking.reference}`,
    paymentStatus: booking.payment === 'paid' ? 'paid' : 'unpaid',

    customerName: booking.customer?.name || '',
    customerEmail: booking.customer?.email || '',
    customerPhone: (booking.customer as { phone?: string })?.phone || booking.customerPhone || '',
    customerAddress: '',

    serviceTitle: booking.service || 'Transfers',
    dateTimeText: `${dateStr} at ${timeStr}`,
    vehicleText: booking.vehicle ? `Vehicle: ${booking.vehicle}` : '',

    pickupLocation: booking.route?.from || '',
    dropoffLocation: booking.route?.to || '',

    items: [
      {
        id: 'trip',
        description: booking.service || 'Transfers',
        subtitle: routeSubtitle,
        qty: 1,
        unitPrice: tripNetPrice,
        isTax: false,
      },
      {
        id: 'vat',
        description: 'Tax Fees',
        subtitle: `${vatRate}% VAT included`,
        unitPrice: vatAmount,
        isTax: true,
      },
    ],

    vatRate,
    manualAmountPaid: null,

    notes: '',
    footerText: `ALPS TAXI • Taxi & Mietwagen Service\nRudolf Spängler Strasse 10, 5020 Salzburg AUSTRIA • VAT / UID: ATU 62231016\nBankverbindung: Raiffeisenbank • IBAN: AT72 3500 0000 1608 1713  BIC: RVSAAT2S`,
  }
}

type InvoiceModalProps = {
  isOpen: boolean
  onClose: () => void
  booking: BookingRecord | null
  vatRate?: VatRate
  onVatRateChange?: (vatRate: VatRate) => void
}

export const InvoiceModal = ({
  isOpen,
  onClose,
  booking,
  vatRate = 10,
  onVatRateChange,
}: InvoiceModalProps) => {
  const invoiceRef = useRef<HTMLDivElement>(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloaded, setDownloaded] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [currentVatRate, setCurrentVatRate] = useState<VatRate>(vatRate)
  const [invoiceData, setInvoiceData] = useState<EditableInvoiceData | null>(null)

  // Initialize or re-initialize data when booking, modal open state, or vatRate prop changes
  useEffect(() => {
    if (booking && isOpen) {
      setInvoiceData(createInitialInvoiceData(booking, vatRate))
      setCurrentVatRate(vatRate)
      setIsEditMode(false)
    }
  }, [booking, isOpen, vatRate])

  // Sync VAT rate switcher
  const handleVatRateSwitch = (newRate: VatRate) => {
    setCurrentVatRate(newRate)
    onVatRateChange?.(newRate)
    setInvoiceData((prev) => {
      if (!prev || !booking) return prev
      const totalAmount = Number(booking.amountEur) || 0
      const { tripNetPrice, vatAmount } = calculateVatBreakdown(totalAmount, newRate)

      const tripIdx = prev.items.findIndex((i) => i.id === 'trip' || i.id === '1')
      const vatIdx = prev.items.findIndex((i) => i.id === 'vat' || isTaxItem(i))

      let updatedItems = prev.items
      if (tripIdx !== -1 && vatIdx !== -1) {
        updatedItems = prev.items.map((item, idx) => {
          if (idx === tripIdx) {
            return {
              ...item,
              unitPrice: tripNetPrice,
            }
          }
          if (idx === vatIdx) {
            return {
              ...item,
              description: 'Tax Fees',
              subtitle: `${newRate}% VAT included`,
              unitPrice: vatAmount,
              qty: null,
              isTax: true,
            }
          }
          return item
        })
      } else {
        updatedItems = prev.items.map((item) => {
          if (item.id === 'vat' || isTaxItem(item)) {
            return { ...item, subtitle: `${newRate}% VAT included`, qty: null, isTax: true }
          }
          return item
        })
      }

      return {
        ...prev,
        vatRate: newRate,
        items: updatedItems,
      }
    })
  }

  // Reset to original booking data
  const handleResetToBooking = () => {
    if (booking) {
      setInvoiceData(createInitialInvoiceData(booking, currentVatRate))
      toast.success('Invoice reset to original booking defaults')
    }
  }

  if (!isOpen || !booking || !invoiceData) return null

  // Helpers to update invoice fields
  const updateField = <K extends keyof EditableInvoiceData>(
    field: K,
    value: EditableInvoiceData[K],
  ) => {
    setInvoiceData((prev) => (prev ? { ...prev, [field]: value } : prev))
  }

  const updateItem = <K extends keyof InvoiceItem>(
    index: number,
    field: K,
    value: InvoiceItem[K],
  ) => {
    setInvoiceData((prev) => {
      if (!prev) return prev
      const newItems = [...prev.items]
      newItems[index] = { ...newItems[index], [field]: value }
      return { ...prev, items: newItems }
    })
  }

  const addItem = () => {
    setInvoiceData((prev) => {
      if (!prev) return prev
      const newItem: InvoiceItem = {
        id: String(Date.now()),
        description: 'Additional Service / Extra',
        subtitle: '',
        qty: 1,
        unitPrice: 0,
        isTax: false,
      }
      return { ...prev, items: [...prev.items, newItem] }
    })
  }

  const removeItem = (index: number) => {
    setInvoiceData((prev) => {
      if (!prev || prev.items.length <= 1) return prev
      return {
        ...prev,
        items: prev.items.filter((_, idx) => idx !== index),
      }
    })
  }

  // Calculated Totals
  const calculatedTotalAmount = invoiceData.items.reduce(
    (sum, item) => sum + getItemAmount(item),
    0,
  )

  const calculatedAmountPaid =
    invoiceData.manualAmountPaid !== null && invoiceData.manualAmountPaid !== undefined
      ? Number(invoiceData.manualAmountPaid)
      : invoiceData.paymentStatus === 'paid'
        ? calculatedTotalAmount
        : 0

  const calculatedBalanceDue = Math.max(0, calculatedTotalAmount - calculatedAmountPaid)

  // Download PDF (automatically renders clean printable document during capture)
  const handleDownloadPdf = async () => {
    if (!invoiceRef.current || isDownloading) return
    setIsDownloading(true)
    const wasEditing = isEditMode
    if (wasEditing) {
      setIsEditMode(false)
      // Allow React to re-render DOM cleanly without input outlines
      await new Promise((resolve) => setTimeout(resolve, 100))
    }

    try {
      if (typeof document !== 'undefined' && document.fonts?.ready) {
        await document.fonts.ready
      }

      const opt = {
        margin: [8, 12, 8, 12] as [number, number, number, number],
        filename: `${invoiceData.invoiceNumber || `Invoice_${booking.reference}`}.pdf`,
        image: {
          type: 'jpeg' as const,
          quality: 0.98,
        },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          windowWidth: 1200,
          scrollX: 0,
          scrollY: 0,
        },
        pagebreak: {
          mode: ['css', 'legacy'],
        },
        jsPDF: {
          unit: 'mm' as const,
          format: 'a4' as const,
          orientation: 'portrait' as const,
        },
      }

      await html2pdf().set(opt).from(invoiceRef.current).save()
      setDownloaded(true)
      setTimeout(() => setDownloaded(false), 3000)
    } catch (err) {
      console.error('Failed to generate PDF via html2pdf, falling back to print dialog', err)
      window.print()
    } finally {
      setIsDownloading(false)
      if (wasEditing) {
        setIsEditMode(true)
      }
    }
  }

  const handlePrint = async () => {
    const wasEditing = isEditMode
    if (wasEditing) {
      setIsEditMode(false)
      await new Promise((resolve) => setTimeout(resolve, 80))
    }
    window.print()
    if (wasEditing) {
      setIsEditMode(true)
    }
  }

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-3 sm:p-6 overflow-y-auto backdrop-blur-sm print:p-0 print:bg-white print:static">
      <div className="relative flex flex-col w-full max-w-[840px] max-h-[96vh] rounded-[20px] bg-[#1E1E1E] shadow-2xl overflow-hidden print:max-w-none print:max-h-none print:rounded-none print:shadow-none print:overflow-visible">
        {/* Top Control Bar (Hidden on Print) */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3.5 bg-[#141414] border-b border-white/10 text-white print:hidden shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#FF6A1A]/20 text-[#FF6A1A]">
              <FileText size={20} />
            </div>
            <div>
              <h3 className="font-host text-[15px] font-semibold leading-tight">
                {isEditMode ? 'Edit Invoice' : 'Invoice Preview'}
              </h3>
              <p className="font-host text-[12px] text-gray-400">
                Booking: #{booking.reference}
              </p>
            </div>
          </div>

          <div className="flex items-center flex-wrap gap-2">
            {/* Mode Switcher: Edit Mode vs Preview Mode */}
            <div className="flex items-center rounded-lg bg-white/10 p-0.5 text-[12px] font-host">
              <button
                type="button"
                onClick={() => setIsEditMode(true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-semibold text-[12px] transition-all cursor-pointer ${
                  isEditMode
                    ? 'bg-[#FF6A1A] text-white shadow-sm'
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                }`}
                title="Edit invoice details directly"
              >
                <Edit2 size={13} />
                <span>Edit Mode</span>
              </button>

              <button
                type="button"
                onClick={() => setIsEditMode(false)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-semibold text-[12px] transition-all cursor-pointer ${
                  !isEditMode
                    ? 'bg-[#719642] text-white shadow-sm'
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                }`}
                title="Preview clean printable invoice"
              >
                <Eye size={13} />
                <span>Preview</span>
              </button>
            </div>

            {/* Reset to booking defaults */}
            <button
              type="button"
              onClick={handleResetToBooking}
              className="flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 px-2.5 py-1.5 font-host text-[12px] font-medium text-gray-200 transition active:scale-95 cursor-pointer"
              title="Reset all fields to original booking data"
            >
              <RotateCcw size={13} />
              <span className="hidden sm:inline">Reset</span>
            </button>

            {/* VAT Rate Switcher */}
            <div className="flex items-center gap-1 bg-white/10 rounded-lg p-0.5 text-[12px] font-host">
              <span className="text-gray-300 px-1 text-[11px] font-medium">VAT:</span>
              <button
                type="button"
                onClick={() => handleVatRateSwitch(10)}
                className={`px-2 py-1 rounded font-bold text-[11px] transition-all cursor-pointer ${
                  currentVatRate === 10
                    ? 'bg-[#FF6A1A] text-white shadow-sm'
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                }`}
                title="10% VAT rate"
              >
                10%
              </button>
              <button
                type="button"
                onClick={() => handleVatRateSwitch(20)}
                className={`px-2 py-1 rounded font-bold text-[11px] transition-all cursor-pointer ${
                  currentVatRate === 20
                    ? 'bg-[#FF6A1A] text-white shadow-sm'
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                }`}
                title="20% VAT rate"
              >
                20%
              </button>
            </div>

            {/* Download PDF button */}
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={isDownloading}
              className="flex items-center gap-1.5 rounded-lg bg-[#719642] px-3.5 py-1.5 font-host text-[12px] font-medium text-white transition hover:bg-[#5f8034] active:scale-95 disabled:opacity-50 shadow-sm cursor-pointer"
              title="Download as PDF"
            >
              {downloaded ? (
                <>
                  <Check size={14} /> Downloaded!
                </>
              ) : isDownloading ? (
                <>
                  <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download size={14} /> PDF
                </>
              )}
            </button>

            {/* Print button */}
            <button
              type="button"
              onClick={handlePrint}
              className="flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 px-3 py-1.5 font-host text-[12px] font-medium text-white transition active:scale-95 cursor-pointer"
              title="Print Invoice"
            >
              <Printer size={14} /> Print
            </button>

            {/* Close button */}
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white transition-colors cursor-pointer"
              aria-label="Close modal"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Scrollable Document Container */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-8 bg-[#2A2A2A] flex justify-center items-start print:p-0 print:bg-white print:overflow-visible">
          {/* Printable Invoice Document */}
          <div
            id="printable-invoice"
            ref={invoiceRef}
            className="w-full max-w-[740px] bg-white text-[#111827] p-8 sm:p-10 shadow-2xl print:shadow-none print:p-0 rounded-[8px] font-sans flex flex-col justify-between border border-gray-200 print:border-none box-border mx-auto transition-all"
          >
            <div>
              {/* Edit Mode Notice Banner */}
              {isEditMode && (
                <div className="mb-6 flex items-center justify-between rounded-lg bg-orange-50 border border-orange-200 px-3.5 py-2.5 text-orange-950 text-xs font-host print:hidden">
                  <div className="flex items-center gap-2">
                    <Edit2 size={14} className="text-[#FF6A1A] shrink-0" />
                    <span>
                      <strong>Edit Mode Active:</strong> You can edit any company details, invoice numbers, customer info, route, items, or totals.
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsEditMode(false)}
                    className="ml-3 shrink-0 rounded bg-[#FF6A1A] px-2.5 py-1 text-white font-semibold text-[11px] hover:bg-[#e05b13] transition shadow-xs cursor-pointer"
                  >
                    Done Editing
                  </button>
                </div>
              )}

              {/* Header: Company Details & Invoice Metadata */}
              <div className="grid grid-cols-[1fr_300px] sm:grid-cols-[1fr_320px] items-start gap-6 sm:gap-8 pb-6 border-b border-gray-200">
                {/* Company Logo & Address */}
                <div className="flex flex-col items-start w-full">
                  <img
                    src="/assets/images/logo.png"
                    alt="Salzburg Cab"
                    className="h-[46px] object-contain mb-3"
                  />

                  {isEditMode ? (
                    <div className="w-full space-y-1.5 text-[12px] font-host">
                      <div>
                        <span className="text-[10px] text-gray-500 uppercase font-semibold block">Company Name</span>
                        <input
                          type="text"
                          value={invoiceData.companyName}
                          onChange={(e) => updateField('companyName', e.target.value)}
                          className="w-full font-bold text-gray-900 text-[13px] border border-dashed border-gray-300 rounded px-1.5 py-1 focus:border-[#FF6A1A] focus:outline-none bg-orange-50/15"
                          placeholder="Company Name"
                        />
                      </div>
                      <div>
                        <span className="text-[10px] text-gray-500 uppercase font-semibold block">Address</span>
                        <input
                          type="text"
                          value={invoiceData.companyAddress}
                          onChange={(e) => updateField('companyAddress', e.target.value)}
                          className="w-full text-gray-700 text-[12px] border border-dashed border-gray-300 rounded px-1.5 py-1 focus:border-[#FF6A1A] focus:outline-none bg-orange-50/15"
                          placeholder="Company Address"
                        />
                      </div>
                      <div>
                        <span className="text-[10px] text-gray-500 uppercase font-semibold block">Phone</span>
                        <input
                          type="text"
                          value={invoiceData.companyPhones}
                          onChange={(e) => updateField('companyPhones', e.target.value)}
                          className="w-full text-gray-700 text-[12px] border border-dashed border-gray-300 rounded px-1.5 py-1 focus:border-[#FF6A1A] focus:outline-none bg-orange-50/15"
                          placeholder="Phone numbers"
                        />
                      </div>
                      <div>
                        <span className="text-[10px] text-gray-500 uppercase font-semibold block">Email</span>
                        <input
                          type="text"
                          value={invoiceData.companyEmails}
                          onChange={(e) => updateField('companyEmails', e.target.value)}
                          className="w-full text-gray-700 text-[12px] border border-dashed border-gray-300 rounded px-1.5 py-1 focus:border-[#FF6A1A] focus:outline-none bg-orange-50/15"
                          placeholder="Email addresses"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="text-[12px] text-gray-600 font-host leading-relaxed">
                      <p className="font-bold text-gray-900 text-[13px]">
                        {invoiceData.companyName}
                      </p>
                      <p>{invoiceData.companyAddress}</p>
                      <p>{invoiceData.companyPhones}</p>
                      <p>{invoiceData.companyEmails}</p>
                    </div>
                  )}
                </div>

                {/* Invoice Metadata */}
                <div className="w-full ml-auto flex flex-col items-end">
                  {/* Invoice Title */}
                  {isEditMode ? (
                    <input
                      type="text"
                      value={invoiceData.invoiceTitle}
                      onChange={(e) => updateField('invoiceTitle', e.target.value)}
                      className="w-full text-right text-[26px] leading-normal font-black tracking-tight text-gray-900 uppercase font-host mb-2 border border-dashed border-gray-300 rounded px-1.5 py-0.5 focus:border-[#FF6A1A] focus:outline-none bg-orange-50/15"
                      placeholder="INVOICE"
                    />
                  ) : (
                    <h1 className="w-full text-right text-[28px] leading-snug font-black tracking-tight text-gray-900 uppercase font-host mb-2">
                      {invoiceData.invoiceTitle || 'INVOICE'}
                    </h1>
                  )}

                  {/* Paid / Unpaid / Pending Badge */}
                  {isEditMode ? (
                    <div className="flex items-center justify-end gap-1.5 mb-4">
                      <span className="text-[11px] text-gray-500 font-host">Payment Status:</span>
                      <select
                        value={invoiceData.paymentStatus}
                        onChange={(e) =>
                          updateField(
                            'paymentStatus',
                            e.target.value as 'paid' | 'unpaid' | 'pending',
                          )
                        }
                        className="rounded border border-gray-300 bg-white px-2 py-1 text-[11px] font-bold uppercase font-host focus:border-[#FF6A1A] focus:outline-none cursor-pointer"
                      >
                        <option value="paid">PAID</option>
                        <option value="unpaid">UNPAID</option>
                        <option value="pending">PENDING</option>
                      </select>
                    </div>
                  ) : (
                    <div className="w-full flex justify-end mb-4">
                      <span
                        style={{
                          display: 'inline-block',
                          textAlign: 'center',
                          lineHeight: '1.4',
                        }}
                        className={`min-w-[96px] rounded-md border px-3 py-1 text-[12px] font-bold uppercase tracking-wider font-host ${
                          invoiceData.paymentStatus === 'paid'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : invoiceData.paymentStatus === 'pending'
                              ? 'bg-blue-50 text-blue-700 border-blue-200'
                              : 'bg-amber-50 text-amber-700 border-amber-200'
                        }`}
                      >
                        {invoiceData.paymentStatus.toUpperCase()}
                      </span>
                    </div>
                  )}

                  {/* Invoice details */}
                  <table className="ml-auto border-collapse font-host text-[13px]">
                    <tbody>
                      <tr>
                        <td className="pr-3 py-1 text-right text-gray-500 whitespace-nowrap">
                          Invoice Number:
                        </td>
                        <td className="py-1 text-right font-bold text-gray-900 font-mono whitespace-nowrap">
                          {isEditMode ? (
                            <input
                              type="text"
                              value={invoiceData.invoiceNumber}
                              onChange={(e) => updateField('invoiceNumber', e.target.value)}
                              className="w-36 text-right font-mono font-bold text-gray-900 border border-dashed border-gray-300 rounded px-1.5 py-0.5 focus:border-[#FF6A1A] focus:outline-none bg-orange-50/15 text-[12px]"
                            />
                          ) : (
                            invoiceData.invoiceNumber
                          )}
                        </td>
                      </tr>

                      <tr>
                        <td className="pr-3 py-1 text-right text-gray-500 whitespace-nowrap">
                          Date:
                        </td>
                        <td className="py-1 text-right font-medium text-gray-900 whitespace-nowrap">
                          {isEditMode ? (
                            <input
                              type="text"
                              value={invoiceData.invoiceDate}
                              onChange={(e) => updateField('invoiceDate', e.target.value)}
                              className="w-36 text-right font-medium text-gray-900 border border-dashed border-gray-300 rounded px-1.5 py-0.5 focus:border-[#FF6A1A] focus:outline-none bg-orange-50/15 text-[12px]"
                            />
                          ) : (
                            invoiceData.invoiceDate
                          )}
                        </td>
                      </tr>

                      <tr>
                        <td className="pr-3 py-1 text-right text-gray-500 whitespace-nowrap">
                          Booking Ref:
                        </td>
                        <td className="py-1 text-right font-semibold text-gray-900 font-mono whitespace-nowrap">
                          {isEditMode ? (
                            <input
                              type="text"
                              value={invoiceData.bookingReference}
                              onChange={(e) => updateField('bookingReference', e.target.value)}
                              className="w-36 text-right font-mono font-semibold text-gray-900 border border-dashed border-gray-300 rounded px-1.5 py-0.5 focus:border-[#FF6A1A] focus:outline-none bg-orange-50/15 text-[12px]"
                            />
                          ) : (
                            invoiceData.bookingReference
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Bill To & Booking Information Grid */}
              <div className="grid grid-cols-2 gap-8 py-5 border-b border-gray-200 font-host">
                {/* Customer Info */}
                <div>
                  <h2 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                    Billed To
                  </h2>
                  {isEditMode ? (
                    <div className="space-y-1.5">
                      <input
                        type="text"
                        value={invoiceData.customerName}
                        onChange={(e) => updateField('customerName', e.target.value)}
                        className="w-full text-[15px] font-bold text-gray-900 border border-dashed border-gray-300 rounded px-1.5 py-1 focus:border-[#FF6A1A] focus:outline-none bg-orange-50/15"
                        placeholder="Customer Full Name"
                      />
                      <input
                        type="email"
                        value={invoiceData.customerEmail}
                        onChange={(e) => updateField('customerEmail', e.target.value)}
                        className="w-full text-[12px] text-gray-600 border border-dashed border-gray-300 rounded px-1.5 py-1 focus:border-[#FF6A1A] focus:outline-none bg-orange-50/15"
                        placeholder="Customer Email Address"
                      />
                      <input
                        type="text"
                        value={invoiceData.customerPhone || ''}
                        onChange={(e) => updateField('customerPhone', e.target.value)}
                        className="w-full text-[12px] text-gray-600 border border-dashed border-gray-300 rounded px-1.5 py-1 focus:border-[#FF6A1A] focus:outline-none bg-orange-50/15"
                        placeholder="Phone Number (optional)"
                      />
                      <input
                        type="text"
                        value={invoiceData.customerAddress || ''}
                        onChange={(e) => updateField('customerAddress', e.target.value)}
                        className="w-full text-[12px] text-gray-600 border border-dashed border-gray-300 rounded px-1.5 py-1 focus:border-[#FF6A1A] focus:outline-none bg-orange-50/15"
                        placeholder="Billing Address / Tax ID (optional)"
                      />
                    </div>
                  ) : (
                    <div>
                      <p className="text-[16px] font-bold text-gray-900 mb-1">
                        {invoiceData.customerName}
                      </p>
                      {invoiceData.customerEmail && (
                        <p className="text-[13px] text-gray-600">{invoiceData.customerEmail}</p>
                      )}
                      {invoiceData.customerPhone && (
                        <p className="text-[13px] text-gray-600">{invoiceData.customerPhone}</p>
                      )}
                      {invoiceData.customerAddress && (
                        <p className="text-[13px] text-gray-600 whitespace-pre-line mt-0.5">
                          {invoiceData.customerAddress}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Booking Details */}
                <div className="text-right">
                  <h2 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                    Booking Details
                  </h2>
                  {isEditMode ? (
                    <div className="space-y-1.5">
                      <input
                        type="text"
                        value={invoiceData.serviceTitle}
                        onChange={(e) => updateField('serviceTitle', e.target.value)}
                        className="w-full text-right text-[14px] font-bold text-gray-900 border border-dashed border-gray-300 rounded px-1.5 py-1 focus:border-[#FF6A1A] focus:outline-none bg-orange-50/15"
                        placeholder="Service Title"
                      />
                      <input
                        type="text"
                        value={invoiceData.dateTimeText}
                        onChange={(e) => updateField('dateTimeText', e.target.value)}
                        className="w-full text-right text-[12px] text-gray-600 border border-dashed border-gray-300 rounded px-1.5 py-1 focus:border-[#FF6A1A] focus:outline-none bg-orange-50/15"
                        placeholder="Date & Time"
                      />
                      <input
                        type="text"
                        value={invoiceData.vehicleText}
                        onChange={(e) => updateField('vehicleText', e.target.value)}
                        className="w-full text-right text-[12px] text-gray-600 border border-dashed border-gray-300 rounded px-1.5 py-1 focus:border-[#FF6A1A] focus:outline-none bg-orange-50/15"
                        placeholder="Vehicle Details (optional)"
                      />
                    </div>
                  ) : (
                    <div>
                      <p className="text-[14px] font-bold text-gray-900 mb-1">
                        {invoiceData.serviceTitle}
                      </p>
                      <p className="text-[13px] text-gray-600">{invoiceData.dateTimeText}</p>
                      {invoiceData.vehicleText && (
                        <p className="text-[13px] text-gray-600">{invoiceData.vehicleText}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Trip Route Box */}
              <div className="my-6 p-4 rounded-lg bg-gray-50 border border-gray-200 font-host">
                <h3 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-3">
                  Trip Route
                </h3>
                <div className="grid grid-cols-2 gap-4 text-[13px]">
                  <div>
                    <span className="text-gray-500 block text-[11px] uppercase font-semibold">
                      Pickup Location
                    </span>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={invoiceData.pickupLocation}
                        onChange={(e) => updateField('pickupLocation', e.target.value)}
                        className="w-full font-semibold text-gray-900 border border-dashed border-gray-300 rounded px-2 py-1 focus:border-[#FF6A1A] focus:outline-none bg-white mt-1 text-[13px]"
                        placeholder="Pickup address"
                      />
                    ) : (
                      <span className="font-semibold text-gray-900 block mt-0.5">
                        {invoiceData.pickupLocation}
                      </span>
                    )}
                  </div>
                  <div>
                    <span className="text-gray-500 block text-[11px] uppercase font-semibold">
                      Drop-off Location
                    </span>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={invoiceData.dropoffLocation}
                        onChange={(e) => updateField('dropoffLocation', e.target.value)}
                        className="w-full font-semibold text-gray-900 border border-dashed border-gray-300 rounded px-2 py-1 focus:border-[#FF6A1A] focus:outline-none bg-white mt-1 text-[13px]"
                        placeholder="Drop-off address"
                      />
                    ) : (
                      <span className="font-semibold text-gray-900 block mt-0.5">
                        {invoiceData.dropoffLocation}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Itemized Table */}
              <div className="py-2">
                <table className="w-full text-left border-collapse font-host">
                  <thead>
                    <tr className="bg-gray-100 border-y border-gray-200 text-[11px] font-bold uppercase tracking-wider text-gray-700">
                      <th className="py-3 px-4">Description</th>
                      <th className="py-3 px-4 text-center w-20">Qty</th>
                      <th className="py-3 px-4 text-right w-28">Price</th>
                      <th className="py-3 px-4 text-right w-32">Amount</th>
                      {isEditMode && (
                        <th className="py-3 px-2 text-center w-12 print:hidden">Del</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-[13px]">
                    {invoiceData.items.map((item, idx) => {
                      const isTax = isTaxItem(item)
                      const itemAmount = getItemAmount(item)
                      return (
                        <tr key={item.id || idx}>
                          <td className="py-3 px-4 align-top">
                            {isEditMode ? (
                              <div className="space-y-1.5">
                                <input
                                  type="text"
                                  value={item.description}
                                  onChange={(e) => updateItem(idx, 'description', e.target.value)}
                                  className="w-full font-bold text-gray-900 border border-dashed border-gray-300 rounded px-1.5 py-1 focus:border-[#FF6A1A] focus:outline-none text-[13px] bg-orange-50/15"
                                  placeholder="Item description"
                                />
                                <input
                                  type="text"
                                  value={item.subtitle || ''}
                                  onChange={(e) => updateItem(idx, 'subtitle', e.target.value)}
                                  className="w-full text-[12px] text-gray-600 border border-dashed border-gray-300 rounded px-1.5 py-0.5 focus:border-[#FF6A1A] focus:outline-none bg-orange-50/15"
                                  placeholder="Route or extra info (optional)"
                                />
                                <input
                                  type="text"
                                  value={item.taxNote || ''}
                                  onChange={(e) => updateItem(idx, 'taxNote', e.target.value)}
                                  className="w-full text-[11px] text-gray-500 border border-dashed border-gray-300 rounded px-1.5 py-0.5 focus:border-[#FF6A1A] focus:outline-none bg-orange-50/15"
                                  placeholder="Tax note (e.g. Tax Rate: 10% VAT)"
                                />
                              </div>
                            ) : (
                              <div>
                                <p className="font-bold text-gray-900">{item.description}</p>
                                {item.subtitle && (
                                  <p className="text-[12px] text-gray-500 mt-0.5">
                                    {item.subtitle}
                                  </p>
                                )}
                                {item.taxNote && (
                                  <p className="text-[11px] text-gray-500 mt-1 font-medium">
                                    {item.taxNote}
                                  </p>
                                )}
                              </div>
                            )}
                          </td>

                          <td className="py-3 px-4 text-center text-gray-600 align-top">
                            {isTax ? (
                              <span className="text-gray-400 font-medium">—</span>
                            ) : isEditMode ? (
                              <input
                                type="number"
                                min="1"
                                value={item.qty ?? 1}
                                onChange={(e) =>
                                  updateItem(
                                    idx,
                                    'qty',
                                    Math.max(1, parseInt(e.target.value) || 1),
                                  )
                                }
                                className="w-14 text-center border border-dashed border-gray-300 rounded px-1 py-1 focus:border-[#FF6A1A] focus:outline-none text-[13px] bg-orange-50/15"
                              />
                            ) : (
                              item.qty ?? 1
                            )}
                          </td>

                          <td className="py-3 px-4 text-right font-mono font-medium text-gray-700 align-top tabular-nums whitespace-nowrap">
                            {isEditMode ? (
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.unitPrice}
                                onChange={(e) =>
                                  updateItem(
                                    idx,
                                    'unitPrice',
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                className="w-24 text-right font-mono border border-dashed border-gray-300 rounded px-1 py-1 focus:border-[#FF6A1A] focus:outline-none text-[13px] bg-orange-50/15"
                              />
                            ) : (
                              formatInvoiceEuro(item.unitPrice)
                            )}
                          </td>

                          <td className="py-3 px-4 text-right font-mono font-bold text-gray-900 align-top tabular-nums whitespace-nowrap">
                            {formatInvoiceEuro(itemAmount)}
                          </td>

                          {isEditMode && (
                            <td className="py-3 px-2 text-center align-top print:hidden">
                              <button
                                type="button"
                                onClick={() => removeItem(idx)}
                                disabled={invoiceData.items.length <= 1}
                                className="p-1 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                                title="Delete line item"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          )}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>

                {/* Add Item Button (in Edit Mode) */}
                {isEditMode && (
                  <div className="mt-3 flex justify-start print:hidden">
                    <button
                      type="button"
                      onClick={addItem}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-gray-300 bg-gray-50 text-[12px] font-semibold text-gray-700 hover:border-[#FF6A1A] hover:bg-orange-50/30 hover:text-[#FF6A1A] transition cursor-pointer"
                    >
                      <Plus size={14} />
                      <span>Add Line Item</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Full-Width Total & Payment Summary */}
              <div className="mt-4 pt-3 border-t-2 border-gray-900 font-host space-y-2.5 pb-6">
                {/* Total Amount (Full Width) */}
                <div className="flex items-center justify-between py-2.5 px-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[16px] font-black text-gray-900 uppercase tracking-tight">
                      Total Amount:
                    </span>
                    <span className="text-[12px] text-gray-500 font-medium">
                      (Includes {currentVatRate}% Austrian VAT)
                    </span>
                  </div>
                  <span className="font-mono text-[22px] sm:text-[24px] font-black text-[#FF6A1A] tabular-nums">
                    {formatInvoiceEuro(calculatedTotalAmount)}
                  </span>
                </div>

                {/* Amount Paid & Balance Due (Full Width Grid) */}
                <div className="grid grid-cols-2 gap-3 text-[13px]">
                  <div className="flex items-center justify-between py-2 px-3.5 bg-emerald-50/40 rounded-lg border border-emerald-100">
                    <span className="text-gray-600 font-medium">Amount Paid:</span>
                    {isEditMode ? (
                      <div className="flex items-center gap-1">
                        <span className="text-gray-400 font-mono text-[12px]">€</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={calculatedAmountPaid}
                          onChange={(e) =>
                            updateField(
                              'manualAmountPaid',
                              parseFloat(e.target.value) || 0,
                            )
                          }
                          className="w-24 text-right font-mono font-medium text-emerald-700 border border-dashed border-gray-300 rounded px-1.5 py-0.5 text-[12px] focus:border-[#FF6A1A] focus:outline-none bg-orange-50/15"
                        />
                      </div>
                    ) : (
                      <span className="font-mono font-bold text-emerald-700 tabular-nums">
                        {formatInvoiceEuro(calculatedAmountPaid)}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-between py-2 px-3.5 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="text-gray-800 font-bold">Balance Due:</span>
                    <span className="font-mono font-bold text-gray-900 text-[14px] tabular-nums">
                      {formatInvoiceEuro(calculatedBalanceDue)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Custom Notes / Payment Terms Section */}
              {isEditMode ? (
                <div className="my-4 p-3.5 bg-gray-50 border border-dashed border-gray-300 rounded-lg">
                  <label className="block text-[11px] font-bold text-gray-600 uppercase mb-1">
                    Invoice Notes / Payment Instructions (optional)
                  </label>
                  <textarea
                    rows={2}
                    value={invoiceData.notes || ''}
                    onChange={(e) => updateField('notes', e.target.value)}
                    className="w-full text-[12px] bg-white text-gray-800 border border-gray-200 rounded p-2 focus:border-[#FF6A1A] focus:outline-none font-host"
                    placeholder="e.g. Payment due within 14 days. Bank: Erste Bank Salzburg, IBAN: AT12 3456 7890 1234, BIC: BKAUATWW..."
                  />
                </div>
              ) : (
                invoiceData.notes && (
                  <div className="my-4 p-3.5 bg-gray-50 rounded-lg border border-gray-200 font-host text-[12px] text-gray-700 whitespace-pre-line">
                    <p className="font-bold text-gray-900 text-[11px] uppercase tracking-wider mb-1">
                      Notes &amp; Payment Terms
                    </p>
                    <p>{invoiceData.notes}</p>
                  </div>
                )
              )}
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-gray-200 text-center font-host text-[11px] text-gray-600 space-y-1 shrink-0 pb-1 leading-relaxed">
              {isEditMode ? (
                <div className="space-y-1">
                  <span className="text-[10px] text-gray-500 uppercase font-semibold block">
                    Invoice Footer (Company, Tax &amp; Banking Details)
                  </span>
                  <textarea
                    rows={3}
                    value={invoiceData.footerText}
                    onChange={(e) => updateField('footerText', e.target.value)}
                    className="w-full text-center text-[11px] text-gray-700 border border-dashed border-gray-300 rounded p-2 focus:border-[#FF6A1A] focus:outline-none bg-orange-50/15 font-host"
                  />
                </div>
              ) : (
                <div className="space-y-1">
                  {invoiceData.footerText.split('\n').map((line, idx) => (
                    <p
                      key={idx}
                      className={
                        idx === 0
                          ? 'font-bold text-gray-900 text-[12px] tracking-tight'
                          : 'text-[11px] text-gray-600'
                      }
                    >
                      {line}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}